<template>
    <div id="createDomain">
        <div class="header mt-5">
            <b class="text-3xl">Etapa de Configuração do DNS</b>
        </div>
        <div class="body mt-5">
            <div class="flex">
                <feather-icon icon="ExternalLinkIcon" svgClasses="w-5 h-5" class="p-5"/>
                <p class="align-self-center">
                    Depois de adicionar seu domínio à sua conta no Pedbot, os valores que você precisa copiar e adicionar ao seu host de domínio serão exibidos abaixo, juntamente com as instruções de como configurar um novo DNS.
                    Você deve <strong>adicionar esses valores (Registro Brevo e Registro DKIM) como novos DNS do seu domínio</strong>. O procedimento será um pouco diferente dependendo do seu host de domínio:
                </p>
            </div>

            <div class="flex justify-center justify-around mt-8 mb-4">
                <div class="max-w-sm bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
                    <div class="p-5 break-words">
                        <h5 class="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Registro Brevo</h5>
                        <p class="mb-3 font-normal text-gray-700 dark:text-gray-400">O código Brevo é necessário para verificar a propriedade do seu domínio. Ele também simplifica o processo de validação de novos remetentes, eliminando a tarefa manual de verificação individual de cada remetente.</p>

                        <div>
                            <vs-list style="list-style-type: none;">
                                <vs-list-header title="Configurações do DNS"/>

                                <li>
                                    <div class="divContainerPreDns">
                                        <strong>Tipo:</strong>
                                        <pre class="limitPreInCreateDns ml-2">TXT</pre>
                                    </div>
                                </li>

                                <li>
                                    <div class="divContainerPreDns">
                                        <strong>Hostname:</strong> <pre class="limitPreInCreateDns ml-2">{{ data.brevo_host_name }}</pre> 
                                    </div>
                                </li>
                                
                                <li>
                                    <div>
                                        <strong>Código Secreto:</strong> <pre class="limitPreInCreateDns">{{ data.brevo_code }}</pre>
                                    </div>
                                </li> <br>

                                <vs-list-header title="Status"/>

                                <li>
                                    <div class="divContainerPreDns">
                                        <strong class="mt-1">Foi validado:</strong>
                                        <vs-chip class="ml-2" :color="data.brevo_validated ? 'success' : 'danger'"> {{ data.brevo_validated ? 'Sim' : 'Não' }} </vs-chip>
                                    </div>
                                </li>
                            </vs-list>
                        </div>
                    </div>
                </div>
    
                <div class="max-w-sm bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
                    <div class="p-5 break-words">
                        <h5 class="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Registro DKIM</h5>
                        <p class="mb-3 font-normal text-gray-700 dark:text-gray-400">O protocolo DKIM permite que você assine seus e-mails com seu nome de domínio, da mesma forma que assina uma carta com sua assinatura. Isso garante ao destinatário que o e-mail é genuinamente seu.</p>

                        <div>
                            <vs-list style="list-style-type: none;">
                                <vs-list-header title="Configurações do DNS"/>

                                <li>
                                    <div class="divContainerPreDns">
                                        <strong>Tipo:</strong>
                                        <pre class="limitPreInCreateDns ml-2">TXT</pre>
                                    </div>
                                </li>
                                    
                                <li>
                                    <div class="divContainerPreDns">
                                        <strong>Hostname:</strong> <pre class="limitPreInCreateDns ml-2">{{ data.dkim_host_name }}</pre> 
                                    </div>
                                </li>
                                
                                <li>
                                    <div>
                                        <strong>Código Secreto:</strong> <pre class="limitPreInCreateDns">{{ data.dkim_code }} </pre>
                                    </div>
                                </li> <br>

                                <vs-list-header title="Status"/>

                                <li>
                                    <div class="divContainerPreDns">
                                        <strong class="mt-1">Foi validado:</strong>
                                        <vs-chip class="ml-2" :color="data.dkim_validated ? 'success' : 'danger'"> {{ data.dkim_validated ? 'Sim' : 'Não' }} </vs-chip>
                                    </div>
                                </li>
                            </vs-list>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex mt-8">
                <feather-icon icon="CheckCircleIcon" svgClasses="w-5 h-5" class="p-5"/>
                <p class="align-self-center">
                    Agora você deve <strong>abrir o painel de controle dos seus domínios </strong>em seu navegedor e deve criar dois registros <strong>DNS</strong>,
                    um registro com as variáveis da <strong>Brevo</strong> e outro com as variáveis <strong>DKIM</strong>. Abaixo está o passo a passo para criar um <strong>DNS</strong> da <strong>Brevo</strong>:
                </p>
            </div>

            <div>
                <div class="flex ml-8 mb-0 pb-0">
                    <feather-icon icon="CornerDownRightIcon" svgClasses="w-5 h-5" class="p-5"/>
                    <p class="align-self-center">
                        Entre na aba de <strong>criação de DNS</strong> em seu host de domínio
                        e crie um novo registro do tipo <strong style="user-select: all">TXT</strong>
                    </p>
                </div>
    
                <div class="flex ml-8 mt-0 pt-0">
                    <feather-icon icon="CornerDownRightIcon" svgClasses="w-5 h-5" class="p-5"/>
                    <p class="align-self-center">
                        O <strong>nome do registro</strong> deve ser <strong style="user-select: all">{{ data.brevo_host_name }}</strong>
                    </p>
                </div>
    
                <div class="flex ml-8 mt-0 pt-0">
                    <feather-icon icon="CornerDownRightIcon" svgClasses="w-5 h-5" class="p-5"/>
                    <p class="align-self-center">
                        O <strong>conteúdo do registro</strong> deve ser <code style="user-select: all"><strong>{{ data.brevo_code }}</strong></code>
                    </p>
                </div>
            </div>

            <div class="flex">
                <feather-icon icon="CheckCircleIcon" svgClasses="w-5 h-5" class="p-5"/>
                <p class="align-self-center">
                    Faça o mesmo processo de criação de <strong>DNS</strong> para o <strong>DKIM</strong> conforme mostrado nas instruções abaixo:
                </p>
            </div>

            <div>
                <div class="flex ml-8 mb-0 pb-0">
                    <feather-icon icon="CornerDownRightIcon" svgClasses="w-5 h-5" class="p-5"/>
                    <p class="align-self-center">
                        Entre na aba de <strong>criação de DNS</strong> em seu host de domínio
                        e crie um novo registro do tipo <strong style="user-select: all">TXT</strong>
                    </p>
                </div>
    
                <div class="flex ml-8 mt-0 pt-0">
                    <feather-icon icon="CornerDownRightIcon" svgClasses="w-5 h-5" class="p-5"/>
                    <p class="align-self-center">
                        O <strong>nome do registro</strong> deve ser <strong style="user-select: all">{{ data.dkim_host_name }}</strong>
                    </p>
                </div>
    
                <div class="flex ml-8 mt-0 pt-0 break-words">
                    <feather-icon icon="CornerDownRightIcon" svgClasses="w-5 h-5" class="p-5"/>
                    <p class="align-self-center" style="max-width: 100%;">
                        O <strong>conteúdo do registro</strong> deve ser <code style="user-select: all"><strong>{{ data.dkim_code }}</strong></code>
                    </p>
                </div>
            </div>

            <div class="flex">
                <feather-icon icon="AlertCircleIcon" svgClasses="w-5 h-5" class="p-5"/>
                <p class="align-self-center">
                    Antes de prosseguir para a úlitma etapa os dois registros DNS serão validados.  Então caso você já tenha criado os dois registros <strong>DNS</strong>, deve aguardar alguns segundos e <strong> clicar no botão "Validar DNS"</strong> para prosseguir para a última etapa.
                </p>
            </div>
        </div>
    </div>
</template>
   
<script>

export default {
name: 'CreateDns',
    data() {
        return {}
    },
    props: {
        data: {
            type: Object,
            required: true
        },
        validateDomain: {
            type: Boolean,
            required: true
        }
    },
    watch: {
        validateDomain(value) {
            if (value) this.$emit('validate', this.data.name);
        }
    },
    emits: [
        'validate'
    ]
}
</script>

<style lang="scss">
.limitPreInCreateDns {
    user-select: all;
    overflow-x: auto;
    max-width: 100%;
    white-space: pre-wrap;
}
.divContainerPreDns {
    display: flex;
    white-space: nowrap;
}
</style>